import { calc } from 'csx';
import { type Styles } from 'react-modal';
import { type CSSProperties, type NestedCSSProperties } from 'typestyle/lib/types';
import DashboardTheme from '../dashboard-theme';

export const modalStyle = (isMobile?: boolean): Styles => ({
  // react-modal's default overlay is a near-white veil, which leaves this popup floating with no
  // separation over an already-light dashboard. The mockup uses a dark scrim.
  overlay: {
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    zIndex: 50,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    boxShadow: '0 20px 50px -20px rgba(0, 0, 0, 0.4)',
    borderRadius: 14,
    border: `1px solid ${DashboardTheme.gray200}`,
    padding: 0,
    // Slightly wider than the mockup's 380px for the one extra column (Check-in time).
    width: 420,
    maxWidth: isMobile ? calc('100% - 30px') : undefined,
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    // react-modal's own default is `overflow: auto`. Left in place it competes with the scrolling
    // body below: the whole box scrolls instead, taking the header and the export button with it,
    // and on a long list you get two nested scrollbars.
    overflow: 'hidden',
    backgroundColor: DashboardTheme.white,
  },
});

export const head: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  padding: '16px 18px 12px',
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
  // A flex item defaults to min-height:auto and refuses to shrink, so without this the header is
  // pushed out of view by a long table instead of the body scrolling.
  flexShrink: 0,
};

export const headText: CSSProperties = {
  minWidth: 0,
};

export const name: CSSProperties = {
  margin: 0,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 14.5,
  fontWeight: 700,
  color: DashboardTheme.navy800,
  wordBreak: 'break-word',
};

export const email: CSSProperties = {
  marginTop: 2,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 12,
  color: DashboardTheme.gray700,
  wordBreak: 'break-all',
};

export const headIcons: CSSProperties = {
  display: 'flex',
  gap: 8,
  flexShrink: 0,
};

export const iconButton: NestedCSSProperties = {
  width: 28,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 7,
  border: `1px solid ${DashboardTheme.gray200}`,
  backgroundColor: DashboardTheme.white,
  color: DashboardTheme.gray500,
  cursor: 'pointer',
  padding: 0,
  $nest: {
    '&:hover:enabled': {
      color: DashboardTheme.navy700,
      borderColor: DashboardTheme.navy700,
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.5,
    },
  },
};

export const body: CSSProperties = {
  padding: '6px 18px 18px',
  overflowY: 'auto',
  // Pairs with flexShrink on the header/footer: this is the one part allowed to shrink, so it is
  // the one that scrolls.
  minHeight: 0,
  flex: '1 1 auto',
};

export const table: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const headerCell: CSSProperties = {
  textAlign: 'left',
  fontSize: 10.5,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  color: DashboardTheme.gray500,
  fontWeight: 700,
  padding: '8px 6px',
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
  whiteSpace: 'nowrap',
  // The header stays put while the rows scroll under it.
  position: 'sticky',
  top: 0,
  background: DashboardTheme.white,
};

export const cell: CSSProperties = {
  padding: '8px 6px',
  fontSize: 12.5,
  color: DashboardTheme.navy800,
  borderBottom: `1px solid ${DashboardTheme.gray100}`,
  verticalAlign: 'top',
};

export const yes: CSSProperties = {
  color: DashboardTheme.green600,
  fontWeight: 700,
};

export const no: CSSProperties = {
  color: DashboardTheme.gray500,
};

export const message: CSSProperties = {
  display: 'block',
  padding: '18px 0',
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13,
  color: DashboardTheme.gray700,
  textAlign: 'center',
};

export const footer: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  padding: '0 18px 18px',
  flexShrink: 0,
};
